import { useEffect, useState } from "react";
import { Eye, Heart, MessageSquare } from "lucide-react";
import { t, fmtNumber } from "../i18n";

// Animated looping mock of the editor + live output, used on the landing hero.
// Pure CSS/JS loop — no video asset needed, always crisp and lightweight.

const CHAT = [
  { a: "Ana Live", t: t("Que overlay lindo! 🔥") },
  { a: "Bruno TV", t: t("Salve da resenha!") },
  { a: "Gabi Stream", t: t("Meta quase batida 👀") },
  { a: "João Vitor", t: "GGWP" },
];

export function HeroPreview() {
  const [viewers, setViewers] = useState(1247);
  const [goal, setGoal] = useState(62);
  const [chatIdx, setChatIdx] = useState(2);

  useEffect(() => {
    const id = setInterval(() => {
      setViewers((v) => Math.max(900, v + Math.floor(Math.random() * 41) - 18));
      setGoal((g) => (g >= 96 ? 38 : g + 2));
      setChatIdx((i) => i + 1);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  const msgs = [CHAT[(chatIdx - 1) % CHAT.length], CHAT[chatIdx % CHAT.length]];

  return (
    <div className="relative select-none" data-testid="hero-preview">
      {/* Editor window frame */}
      <div className="rounded-md border border-border/70 bg-card shadow-[0_24px_80px_-24px_rgba(0,122,255,0.25)] overflow-hidden">
        {/* Title bar */}
        <div className="h-8 flex items-center gap-1.5 px-3 border-b border-border/60 bg-black/30">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-[10px] font-mono text-muted-foreground tracking-widest">LIVEGRAFX · EDITOR</span>
          <span className="ml-auto text-[9px] font-mono text-emerald-400 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" /> {t("AO VIVO")}
          </span>
        </div>

        {/* Canvas 16:9 with checker background */}
        <div className="relative checker-bg" style={{ aspectRatio: "16/9" }}>
          {/* Lower third — slides in and out in a loop */}
          <div className="absolute left-[5%] bottom-[12%] hero-lowerthird">
            <div className="bg-primary text-primary-foreground font-display font-extrabold text-[clamp(10px,1.6vw,16px)] px-3 py-1 rounded-sm w-max">
              MARIA SILVA
            </div>
            <div className="bg-black/80 text-white/90 text-[clamp(8px,1.1vw,12px)] px-3 py-0.5 rounded-sm w-max mt-0.5">
              {t("Repórter · Direto do estúdio")}
            </div>
          </div>

          {/* Viewer counter — ticks live */}
          <div className="absolute right-[5%] top-[8%] bg-black/75 rounded-sm px-2.5 py-1.5 flex items-center gap-1.5 hero-float">
            <Eye className="h-3 w-3 text-red-500" />
            <span className="font-mono font-bold text-white text-[clamp(9px,1.3vw,14px)] tabular-nums" data-testid="hero-viewers">
              {fmtNumber(viewers)}
            </span>
            <span className="text-white/50 text-[clamp(7px,0.9vw,10px)]">{t("assistindo")}</span>
          </div>

          {/* Goal bar — fills in a loop */}
          <div className="absolute left-[5%] top-[8%] w-[34%] bg-black/75 rounded-sm px-2.5 py-1.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white/80 text-[clamp(7px,1vw,11px)] font-semibold flex items-center gap-1">
                <Heart className="h-2.5 w-2.5 text-[#9146FF]" /> {t("Meta de seguidores")}
              </span>
              <span className="text-white/50 font-mono text-[clamp(7px,0.9vw,10px)]">{goal}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
              <div className="h-full rounded-full bg-[#9146FF] transition-all duration-1000 ease-out" style={{ width: `${goal}%` }} />
            </div>
          </div>

          {/* Chat bubbles — rotate */}
          <div className="absolute right-[5%] bottom-[12%] w-[36%] space-y-1">
            {msgs.map((m, i) => (
              <div key={`${chatIdx}-${i}`} className="bg-black/75 rounded-sm px-2 py-1 hero-chat-in flex items-start gap-1.5" style={{ animationDelay: `${i * 120}ms` }}>
                <MessageSquare className="h-2.5 w-2.5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-[clamp(7px,1vw,11px)] leading-tight text-white/85 min-w-0">
                  <span className="text-primary font-semibold">{m.a}</span> {m.t}
                </p>
              </div>
            ))}
          </div>

          {/* Selection outline moving between elements */}
          <div className="absolute hero-selection pointer-events-none border-2 border-primary/90 rounded-[2px]">
            <span className="absolute -top-1 -left-1 h-1.5 w-1.5 bg-primary" />
            <span className="absolute -top-1 -right-1 h-1.5 w-1.5 bg-primary" />
            <span className="absolute -bottom-1 -left-1 h-1.5 w-1.5 bg-primary" />
            <span className="absolute -bottom-1 -right-1 h-1.5 w-1.5 bg-primary" />
          </div>
        </div>
      </div>

      {/* Caption chip */}
      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-secondary border border-border/70 rounded-full px-4 py-1.5 text-[10px] font-mono tracking-widest text-muted-foreground whitespace-nowrap">
        {t("TUDO ATUALIZA EM TEMPO REAL NO OBS")}
      </div>
    </div>
  );
}
